import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconArrowDown,
  IconArrowUp,
  IconEdit,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useDeletePersonal,
  usePersonal,
  useReorderPersonal,
} from '@/api/usePersonal';
import { usePersonalbedarf } from '@/api/useBelegung';
import { usePlan } from '@/api/usePlaene';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorState } from '@/components/ErrorState';
import { LoadingState } from '@/components/LoadingState';
import type { PersonalRow, Tarifart, UUID } from '@/api/types';
import {
  ausbildungAnteil,
  ausbildungAnteilJahresmittel,
  ausbildungsjahreImFoerderjahr,
  aktiveMonateImFoerderjahr,
  hatAusbildungszeitraum,
} from '@/constants/ausbildung';
import { fachkraftebedarfMin } from '@/lib/fachkraefte';
import { FZM_CAP_RATIO, classifyFzmRows } from '@/lib/fzm';
import { PersonalEditDialog } from './PersonalEditDialog';
import { KostenBereich } from './KostenBereich';
import { describeApiError, formatAktiv, formatEuro } from './utils';

interface PersonalkostenTabProps {
  planId: UUID;
  disabled: boolean;
  onMutated: () => void;
}

export function PersonalkostenTab({ planId, disabled, onMutated }: PersonalkostenTabProps) {
  const { t, i18n } = useTranslation(['personal', 'common', 'errors']);

  const personal = usePersonal(planId);
  const personalbedarf = usePersonalbedarf(planId);
  const plan = usePlan(planId);
  // Bezugsjahr fuer den Ausbildungsverlauf. Solange der Plan noch laedt,
  // faellt die Anzeige auf das laufende Jahr zurueck — sichtbar wird das
  // nur fuer einen Wimpernschlag.
  const foerderjahr = plan.data?.foerderjahr ?? new Date().getFullYear();

  /** "Aktiv"-Spalte: bei Ausbildungen aus dem Zeitraum, sonst wie bisher. */
  const aktivText = (row: PersonalRow, jahr: number): string => {
    if (row.tarifart !== 'ausbildung' || !hatAusbildungszeitraum(row)) {
      return formatAktiv(row.aktiv_ab_monat, row.aktiv_bis_monat);
    }
    const monate = aktiveMonateImFoerderjahr(row, jahr);
    if (monate === null) return '—';
    return formatAktiv(
      monate.ab === 1 ? null : monate.ab,
      monate.bis === 12 ? null : monate.bis,
    );
  };

  /** "Jahr"-Spalte bei Ausbildungen mit Zeitraum, z.B. "1. / 2.". */
  const ausbildungsjahrText = (row: PersonalRow, jahr: number): string | null => {
    if (row.tarifart !== 'ausbildung' || !hatAusbildungszeitraum(row)) return null;
    const jahre = ausbildungsjahreImFoerderjahr(row, jahr);
    if (jahre.length === 0) return '—';
    return jahre.map((j) => `${j}.`).join(' / ');
  };
  const deletePersonal = useDeletePersonal(planId);
  const reorderPersonal = useReorderPersonal(planId);

  // FzM-Cap: 30 % vom Mindestpersonalbedarf nach HKJGB (= h_woche ohne
  // mpz-Aufschlag, im Jahresdurchschnitt).
  const fzmLimit = (() => {
    const monatswerte = personalbedarf.data?.monatswerte ?? [];
    const mpzFactor =
      plan.data?.mpz_anteil != null ? Number(plan.data.mpz_anteil) : null;
    return fachkraftebedarfMin(monatswerte, mpzFactor) * FZM_CAP_RATIO;
  })();
  const fzmClassMap = classifyFzmRows(personal.data ?? [], fzmLimit);

  /** Swap row `rowId` mit dem nächsten Nachbarn derselben Tarifart in der
   *  Gesamt-Liste (so bleibt die Reorder-API kompatibel). */
  const movePersonal = (rowId: UUID, delta: -1 | 1) => {
    const rows = personal.data;
    if (!rows) return;
    const fromIdx = rows.findIndex((r) => r.id === rowId);
    if (fromIdx < 0) return;
    const tarifart = rows[fromIdx].tarifart;
    let targetIdx = fromIdx + delta;
    while (targetIdx >= 0 && targetIdx < rows.length) {
      if (rows[targetIdx].tarifart === tarifart) break;
      targetIdx += delta;
    }
    if (targetIdx < 0 || targetIdx >= rows.length) return;
    const ids = rows.map((r) => r.id);
    [ids[fromIdx], ids[targetIdx]] = [ids[targetIdx], ids[fromIdx]];
    void reorderPersonal.mutateAsync(ids).catch((err) => {
      notifications.show({
        color: 'red',
        title: t('errors:generic'),
        message: describeApiError(err) || t('errors:generic'),
      });
    });
  };

  const [personalEdit, setPersonalEdit] = useState<PersonalRow | null>(null);
  const [personalCreate, setPersonalCreate] = useState<Tarifart | null>(null);
  const [personalDelete, setPersonalDelete] = useState<PersonalRow | null>(null);

  const handleError = (err: unknown) =>
    notifications.show({
      color: 'red',
      title: t('errors:generic'),
      message: describeApiError(err) || t('errors:generic'),
    });

  const renderSection = (tarifart: Tarifart, titleKey: string) => {
    const all = personal.data ?? [];
    const filtered = all.filter((r) => r.tarifart === tarifart);
    return (
      <Paper withBorder p="md" key={tarifart}>
        <Group justify="space-between" mb="sm">
          <Title order={4}>{t(titleKey)}</Title>
          {!disabled ? (
            <Button
              leftSection={<IconPlus size={14} />}
              size="xs"
              onClick={() => setPersonalCreate(tarifart)}
            >
              {t('personal:stelle.add')}
            </Button>
          ) : null}
        </Group>
        {personal.isLoading ? <LoadingState height={120} /> : null}
        {personal.isError ? <ErrorState error={personal.error} /> : null}
        {personal.data && filtered.length === 0 ? (
          <Text c="dimmed">{t('common:state.empty')}</Text>
        ) : null}
        {filtered.length > 0 ? (
          <Table.ScrollContainer minWidth={760}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('personal:col.name')}</Table.Th>
                  <Table.Th>{t('personal:col.kategorie')}</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>{t('personal:col.std')}</Table.Th>
                  <Table.Th>{t('personal:col.fachkraft')}</Table.Th>
                  <Table.Th>Aktiv</Table.Th>
                  <Table.Th>{t('personal:col.eg')}</Table.Th>
                  <Table.Th>
                    {tarifart === 'ausbildung'
                      ? t('personal:col.jahr')
                      : t('personal:col.stufe')}
                  </Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>{t('personal:col.brutto')}</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>{t('personal:col.kostenAg')}</Table.Th>
                  <Table.Th aria-label={t('personal:col.aktionen')} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filtered.map((row, idx) => (
                  <Table.Tr key={row.id}>
                    <Table.Td style={{ fontWeight: 500 }}>
                      {row.name && row.name.trim() !== '' ? (
                        row.name
                      ) : (
                        <Text c="dimmed" size="sm" fs="italic">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>{row.kategorie}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{row.std_woche}</Table.Td>
                    <Table.Td>
                      {row.tarifart === 'ausbildung' ? (
                        <Badge color="lag" variant="light">
                          {`${Math.round(
                            (hatAusbildungszeitraum(row)
                              ? ausbildungAnteilJahresmittel(row, foerderjahr)
                              : ausbildungAnteil(
                                  row.eg_gruppe,
                                  row.eg_stufe,
                                  row.piva_landesgefoerdert,
                                )) * 100,
                          )} %`}
                        </Badge>
                      ) : row.fzm ? (
                        (() => {
                          const cls = fzmClassMap.get(row.id);
                          if (cls?.status === 'violator') {
                            return (
                              <Stack gap={0} align="flex-start">
                                <Badge color="red" variant="filled">
                                  {t('common:yes')}
                                </Badge>
                                <Text size="xs" c="red" style={{ whiteSpace: 'nowrap' }}>
                                  {t('personal:col.fachkraft.fzmExceeded', {
                                    rest: (cls.restStdWoche ?? 0).toLocaleString('de-DE', {
                                      minimumFractionDigits: 1,
                                      maximumFractionDigits: 2,
                                    }),
                                  })}
                                </Text>
                              </Stack>
                            );
                          }
                          if (cls?.status === 'over') {
                            return (
                              <Badge color="gray" variant="light">
                                {t('personal:col.fachkraft.fzmNo')}
                              </Badge>
                            );
                          }
                          return (
                            <Badge color="green" variant="light">
                              {t('personal:col.fachkraft.fzmYes')}
                            </Badge>
                          );
                        })()
                      ) : (
                        <Badge color={row.fachkraft ? 'green' : 'gray'} variant="light">
                          {row.fachkraft ? t('common:yes') : t('common:no')}
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>
                      {/* Bei einer Ausbildung mit Zeitraum bestimmt dieser
                          die aktiven Monate, nicht die Eintritts- und
                          Austrittsfelder. */}
                      {aktivText(row, foerderjahr)}
                    </Table.Td>
                    <Table.Td>{row.eg_gruppe}</Table.Td>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>
                      {/* Ohne Zeitraum die eingetragene Stufe; mit Zeitraum
                          die Ausbildungsjahre, die in dieses Foerderjahr
                          fallen — meist zwei. */}
                      {ausbildungsjahrText(row, foerderjahr) ?? row.eg_stufe}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {row.brutto_monatlich !== null
                        ? formatEuro(row.brutto_monatlich, i18n.language)
                        : '—'}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {row.kosten_ag_monatlich !== null
                        ? formatEuro(row.kosten_ag_monatlich, i18n.language)
                        : '—'}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        {!disabled ? (
                          <>
                            <Tooltip label="Nach oben">
                              <ActionIcon
                                variant="subtle"
                                onClick={() => movePersonal(row.id, -1)}
                                disabled={idx === 0 || reorderPersonal.isPending}
                                aria-label="Nach oben"
                              >
                                <IconArrowUp size={14} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Nach unten">
                              <ActionIcon
                                variant="subtle"
                                onClick={() => movePersonal(row.id, 1)}
                                disabled={
                                  idx === filtered.length - 1 || reorderPersonal.isPending
                                }
                                aria-label="Nach unten"
                              >
                                <IconArrowDown size={14} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label={t('common:actions.edit')}>
                              <ActionIcon
                                variant="subtle"
                                onClick={() => setPersonalEdit(row)}
                                aria-label={t('common:actions.edit')}
                              >
                                <IconEdit size={14} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label={t('common:actions.delete')}>
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                onClick={() => setPersonalDelete(row)}
                                aria-label={t('common:actions.delete')}
                              >
                                <IconTrash size={14} />
                              </ActionIcon>
                            </Tooltip>
                          </>
                        ) : null}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        ) : null}
      </Paper>
    );
  };

  return (
    <Stack>
      {renderSection('sue', 'personal:section.title.sue')}
      {renderSection('ausbildung', 'personal:section.title.ausbildung')}
      {renderSection('vka', 'personal:section.title.vka')}

      <PersonalEditDialog
        opened={personalCreate !== null}
        planId={planId}
        position={null}
        tarifart={personalCreate ?? 'sue'}
        foerderjahr={foerderjahr}
        onClose={() => setPersonalCreate(null)}
        onSaved={onMutated}
      />
      <PersonalEditDialog
        opened={personalEdit !== null}
        planId={planId}
        position={personalEdit}
        foerderjahr={foerderjahr}
        onClose={() => setPersonalEdit(null)}
        onSaved={onMutated}
      />

      {personalDelete ? (
        <ConfirmDialog
          opened
          title={t('personal:stelle.remove')}
          message={t('personal:delete.message')}
          confirmLabel={t('common:actions.delete')}
          danger
          onCancel={() => setPersonalDelete(null)}
          onConfirm={async () => {
            const target = personalDelete;
            setPersonalDelete(null);
            try {
              await deletePersonal.mutateAsync(target.id);
              notifications.show({ color: 'teal', message: t('personal:info.deleted') });
              onMutated();
            } catch (err) {
              handleError(err);
            }
          }}
        />
      ) : null}

      {/* Sonstige Personalkosten (Migration 0027): keine Stellen, sondern
          Kostenpunkte — Beitraege zur Berufsgenossenschaft, Reisekosten,
          Fortbildung, dazu "Besondere Kosten" mit eigenen Positionen. Sie
          stehen hier statt bei den Sachkosten, weil sie in der Rechnung als
          Personalkosten zaehlen (Kostenart "personal_sonstiges"). */}
      <KostenBereich
        planId={planId}
        disabled={disabled}
        onMutated={onMutated}
        bereichCode="personal"
        titel={t('personal:sonstige.title')}
        summeLabel={t('personal:sonstige.summe')}
        themenblockLabel={t('personal:sonstige.title')}
      />
    </Stack>
  );
}
